import { DEFAULT_SALON_PUBLIC_LAYOUT } from './salonPublicLayout';
import { DEFAULT_SALON_PUBLIC_THEME } from './salonPublicTheme';


const clone = (value) => JSON.parse(JSON.stringify(value));

const deepFreeze = (value) => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach(deepFreeze);
  }
  return value;
};

const createPreset = (
  theme = DEFAULT_SALON_PUBLIC_THEME,
  buttonDirection = {},
  contentDirection = {},
) => ({
  theme: clone(theme),
  layout: {
    ...clone(DEFAULT_SALON_PUBLIC_LAYOUT),
    buttons: {
      ...clone(DEFAULT_SALON_PUBLIC_LAYOUT.buttons),
      ...buttonDirection,
    },
    content: {
      ...clone(DEFAULT_SALON_PUBLIC_LAYOUT.content),
      ...contentDirection,
    },
  },
});

export const DEFAULT_SALON_PUBLIC_TEMPLATE_KEY = 'saloone_signature';

export const SALON_PUBLIC_TEMPLATE_KEYS = Object.freeze([
  'saloone_signature',
  'atelier_muse',
  'maison_eclat',
  'pure_studio',
  'harmonie_clinique',
  'gloss_lab',
  'cercle_heritage',
  'ecrin_serein',
  'neo_derma',
]);

export const SALON_PUBLIC_TEMPLATE_PILOT_KEYS = Object.freeze([
  'saloone_signature',
  'atelier_muse',
  'maison_eclat',
]);

const template = ({
  key,
  name,
  translationKey,
  metierTags,
  structuralControls = false,
  theme,
  buttonDirection,
  contentDirection,
}) => deepFreeze({
  key,
  name,
  translationKey,
  metierTags,
  capabilities: { structuralControls },
  preset: createPreset(theme, buttonDirection, contentDirection),
});

export const SALON_PUBLIC_TEMPLATES = deepFreeze({
  saloone_signature: template({
    key: 'saloone_signature',
    name: 'SALOONE Signature',
    translationKey: 'salonTemplates.salooneSignature',
    metierTags: ['signature', 'versatile'],
    structuralControls: true,
  }),
  atelier_muse: template({
    key: 'atelier_muse',
    name: 'Atelier Muse',
    translationKey: 'salonTemplates.atelierMuse',
    metierTags: ['beauty', 'editorial'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#9B715D',
      secondary_color: '#D8B59F',
      background_color: '#F4E9DF',
      font_key: 'playfair_display',
    },
  }),
  maison_eclat: template({
    key: 'maison_eclat',
    name: 'Maison Éclat',
    translationKey: 'salonTemplates.maisonEclat',
    metierTags: ['hair', 'luxury'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#814B59',
      secondary_color: '#C9919F',
      background_color: '#F7F1EF',
      font_key: 'playfair_display',
    },
    buttonDirection: { appearance: 'filled', shape: 'pill', size: 'standard' },
  }),
  pure_studio: template({
    key: 'pure_studio',
    name: 'Pure Studio',
    translationKey: 'salonTemplates.pureStudio',
    metierTags: ['studio', 'minimal'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#E06245',
      secondary_color: '#29231F',
      background_color: '#FFFDF9',
      font_key: 'montserrat',
    },
    buttonDirection: { appearance: 'filled', shape: 'rounded', size: 'compact' },
  }),
  harmonie_clinique: template({
    key: 'harmonie_clinique',
    name: 'Harmonie Clinique',
    translationKey: 'salonTemplates.harmonieClinique',
    metierTags: ['wellness', 'clinic'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#0B5B5C',
      secondary_color: '#123F3F',
      background_color: '#FBFAF6',
      font_key: 'open_sans',
    },
    contentDirection: { services: 'grid', service_columns: 3 },
  }),
  gloss_lab: template({
    key: 'gloss_lab',
    name: 'Gloss Lab',
    translationKey: 'salonTemplates.glossLab',
    metierTags: ['nails', 'creative'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#FF92BD',
      secondary_color: '#211921',
      background_color: '#FFF5EF',
      font_key: 'poppins',
    },
    buttonDirection: { appearance: 'filled', shape: 'square', size: 'compact' },
  }),
  cercle_heritage: template({
    key: 'cercle_heritage',
    name: 'Cercle Héritage',
    translationKey: 'salonTemplates.cercleHeritage',
    metierTags: ['barber', 'heritage'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#D5B477',
      secondary_color: '#151515',
      background_color: '#151515',
      font_key: 'classic',
    },
    buttonDirection: { appearance: 'outlined', shape: 'square', size: 'standard' },
  }),
  ecrin_serein: template({
    key: 'ecrin_serein',
    name: 'Écrin Serein',
    translationKey: 'salonTemplates.ecrinSerein',
    metierTags: ['spa', 'serene'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#52685A',
      secondary_color: '#9EAA9C',
      background_color: '#ECECE5',
      font_key: 'classic',
    },
    buttonDirection: { appearance: 'filled', shape: 'pill', size: 'standard' },
  }),
  neo_derma: template({
    key: 'neo_derma',
    name: 'Neo Derma',
    translationKey: 'salonTemplates.neoDerma',
    metierTags: ['dermatology', 'modern'],
    theme: {
      ...DEFAULT_SALON_PUBLIC_THEME,
      primary_color: '#28776E',
      secondary_color: '#173C3A',
      background_color: '#F7F8F8',
      font_key: 'saloone',
    },
  }),
});

export const getSalonPublicTemplate = (candidate) => (
  Object.prototype.hasOwnProperty.call(SALON_PUBLIC_TEMPLATES, candidate)
    ? SALON_PUBLIC_TEMPLATES[candidate]
    : SALON_PUBLIC_TEMPLATES[DEFAULT_SALON_PUBLIC_TEMPLATE_KEY]
);

export const createSalonTemplateAppearance = (candidate) => {
  const entry = getSalonPublicTemplate(candidate);
  return clone({
    template_key: entry.key,
    theme: entry.preset.theme,
    layout: entry.preset.layout,
  });
};
